import { Link } from "react-router";
import { ArrowRight, Users } from "lucide-react";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { useAdminMetrics } from "@/shared/lib/admin";
import { StatCard, formatUptime } from "./StatCard";

export function AdminDashboard() {
  const metrics = useAdminMetrics();
  const loading = metrics.isLoading;
  const data = metrics.data;

  return (
    <Section spacing="md">
      <Container size="6xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="type-heading-lg text-text">Admin</h1>
            <Button asChild variant="secondary">
              <Link to="/app/admin/users">
                <Users aria-hidden className="h-4 w-4" />
                Manage users
              </Link>
            </Button>
          </div>

          {metrics.isError ? (
            <p className="type-body-base text-danger-strong">
              Couldn't load metrics: {metrics.error.message}
            </p>
          ) : (
            // Static chrome (labels + the User-management tile) stays rendered;
            // only the metric values skeleton while loading (design.md §14).
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Total users" loading={loading} value={data?.totalUsers} />
                <StatCard
                  label="Active now"
                  loading={loading}
                  value={data?.activeUsers}
                  hint="Users with a live session"
                />
                <StatCard
                  label="Suspended"
                  loading={loading}
                  value={data?.suspendedUsers}
                />
                <StatCard
                  label="New (30 days)"
                  loading={loading}
                  value={data?.newUsersLast30d}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <StatCard
                  label="System uptime"
                  loading={loading}
                  value={data ? formatUptime(data.uptimeSeconds) : undefined}
                  hint={
                    data
                      ? `Since ${new Date(data.startedAt).toLocaleString()} (this API instance)`
                      : undefined
                  }
                />
                <Link
                  to="/app/admin/users"
                  className="flex items-center justify-between gap-4 rounded-2xl glass-surface p-6 transition-colors duration-150 hover:bg-white/40"
                >
                  <span className="flex flex-col gap-1">
                    <span className="type-label-sm tracking-wider uppercase text-text-muted">
                      User management
                    </span>
                    <span className="type-body-base text-text">
                      View, search, edit, suspend, and create accounts
                    </span>
                  </span>
                  <ArrowRight aria-hidden className="h-5 w-5 shrink-0 text-text-muted" />
                </Link>
              </div>
            </>
          )}
        </div>
      </Container>
    </Section>
  );
}
